import { Injectable, InputSignal } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from "@angular/common/http";
import { environment } from "@env/environment";
import {
  EvaluationVehicleFilter,
  EvaluationVehicleResponse,
  EvaluationVehicle,
  resolveEvaluationVehicleSegment,
  RecentHistoryItem,
  IntegrationHistoryItem,
  EvalStatus,
  EvalTypeLabel,
  EvalReasonArchiveHelper,
  NegotiationStatus,
  EvaluationLeadResponse,
  Owner,
  StatusEvaluation,
  EvalOptions,
  EvalTruckOptions,
  EvalMotorcycleOptions,
  OwnerPhoneConfirmationRequest,
  EvalVehicleLookupResponse,
  EvaluatorOption,
} from "@app-model/evaluation";
import { map } from "rxjs/operators";
import { BehaviorSubject, Observable, pipe, Subject } from "rxjs";
import { BuyPriceDetails, SellPriceDetails } from "@app-model/pricing";
import { EvalDealerHelper } from "@app/views/private/admin/model/advertisers";
import { FileUploadResult, FileUploadStatus, IdName, VehicleNumberPlateStatus } from "./oem-dealer.service";
import { DefaultStatus } from "@app-model/login";
import { Color, CountryOrigin, FuelEnum, ReportStatus } from "@app-model/vehicle";
import { VehicleUpdateStatus } from "@app/views/public/online-evaluation/online-eval.model";
import { EvaluationHistoryFilter } from "@app-model/evaluation-history";
import * as moment from "moment";
import { AnalysisSummary, AwaitingEntrySummary, InventorySummary, StockHealth } from "@app/views/private/shopkeeper/oem-dealer/vehicle/stock-pricing/stock-pricing.types";
import { buildUpdatePricePayload, resolveEvaluationPriceValue } from "@app-utils/eval-price.utils";
import { ImageUploadUrlService } from "@app-services/image-upload-url.service";
import { EvalPriceChangeHistoryItem } from "@app-model/eval-price-change-history";

@Injectable({
  providedIn: "root",
})
export class EvaluationService {
  constructor(
    private http: HttpClient,
    private imageUploadUrlService: ImageUploadUrlService,
  ) { }

  private axleChanged = new Subject<void>();

  notifyAxleChanged() {
    this.axleChanged.next();
  }

  onAxleChanged(): Observable<void> {
    return this.axleChanged.asObservable();
  }

  search(
    filter: EvaluationVehicleFilter
  ): Observable<EvaluationVehicleResponse> {
    return this.http
      .post(`${environment.api}/evaluation/search`, filter)
      .pipe(map((response: EvaluationVehicleResponse) => this.mapEvaluationVehicleResponse(response)));
  }

  searchEvalDealers(query: string, oemDealerId?: string, noVirtual?: boolean): Observable<EvalDealerHelper[]> {
    let httpParams = new HttpParams();
    if (query) {
      httpParams = httpParams.append('query', query);
    }
    if (oemDealerId) {
      httpParams = httpParams.append('oemDealerId', oemDealerId);
    }

    if(noVirtual){
      httpParams = httpParams.append('virtual', "false")
    }

    return this.http
      .get(`${environment.api}/evaluation/searchEvalDealers`, { params: httpParams })
      .pipe(map((response: EvalDealerHelper[]) => response));
  }

  getSellersMyEvalDealer(vehicleId: string): Observable<IdName[]> {
    return this.http
      .get(`${environment.api}/evaluation/sellersMyEvalDealer/${vehicleId}`)
      .pipe(map((response: IdName[]) => response));
  }

  getEvaluatorsMyEvalDealer(vehicleId: string): Observable<EvaluatorOption[]> {
    return this.http.get<EvaluatorOption[]>(
      `${environment.api}/evaluation/evaluatorsMyEvalDealer/${vehicleId}`,
    );
  }

  changeEvaluator(vehicleId: string, evaluatorId: string): Observable<DefaultStatus> {
    return this.http.post<DefaultStatus>(
      `${environment.api}/evaluation/changeEvaluator/${vehicleId}`,
      { evaluatorId },
    );
  }

  getEvalDealers(vehicleId: string): Observable<IdName[]> {
    return this.http
      .get(`${environment.api}/evaluation/evalDealers/${vehicleId}`)
      .pipe(map((response: IdName[]) => response));
  }


  sendScheduledToEvaluator(evalId: string): Observable<DefaultStatus> {
    return this.http
      .post(`${environment.api}/evaluation/sendScheduledToEval/${evalId}`, undefined)
      .pipe(map((response: DefaultStatus) => response));
  }

  searchTransfer(
    filter: EvaluationVehicleFilter
  ): Observable<EvaluationVehicleResponse> {
    return this.http
      .post(`${environment.api}/oemDealer/searchEval`, filter)
      .pipe(map((response: EvaluationVehicleResponse) => this.mapEvaluationVehicleResponse(response)));
  }

  cancel(vehicleId: string) {
    return this.http.post(
      `${environment.api}/evaluation/cancel/${vehicleId}`,
      null
    );
  }

  archive(vehicleId: string, reasonArchive: string) {
    return this.http.post(
      `${environment.api}/evaluation/archive/${vehicleId}/${reasonArchive}`, {}
    );
  }

  fullHistory(vehicleId: string): Observable<RecentHistoryItem[]> {
    return this.http
      .get(`${environment.api}/evaluation/fullHistory/${vehicleId}`)
      .pipe(map((response: any) => response));
  }

  fullIntegrationHistory(vehicleId: string): Observable<IntegrationHistoryItem[]> {
    return this.http
      .get(`${environment.api}/evaluation/fullIntegrationHistory/${vehicleId}`)
      .pipe(map((response: any) => response));
  }

  getPriceChangeHistory(vehicleId: string): Observable<EvalPriceChangeHistoryItem[]> {
    return this.http
      .get<EvalPriceChangeHistoryItem[]>(`${environment.api}/evaluation/pricing/priceHistory/${vehicleId}`)
      .pipe(map((response) => response ?? []));
  }

  resynchronizeEval(evalVehicleId: string): Observable<DefaultStatus> {
    return this.http.get<DefaultStatus>(
      `${environment.api}/oemDealer/resynchronize-eval/${evalVehicleId}`,
    );
  }

  getVehicleById(vehicleId: string, viewStat: boolean): Observable<EvaluationVehicle> {
    return this.http
      .get(`${environment.api}/evaluation/get/${vehicleId}?viewStat=${viewStat}`)
      .pipe(map((response: any) => this.mapEvaluationVehicle(response)));
  }

  getVehicleByIdOemDealer(vehicleId: string): Observable<EvaluationVehicle> {
    return this.http
      .get(`${environment.api}/oemDealer/getEval/${vehicleId}`)
      .pipe(map((response: any) => this.mapEvaluationVehicle(response)));
  }

  saveEvaluation(payload) {
    return this.http.post(
      `${environment.api}/evaluation/saveEvaluation`,
      payload
    );
  }

  saveAndFinishEval(payload) {
    return this.http.post(
      `${environment.api}/evaluation/saveAndFinishEval`,
      payload
    );
  }

  checkNumberPlate(numberPlate: string, loadPreviousEvals: boolean = true) {
    return this.http.post<EvalVehicleLookupResponse>(
      `${environment.api}/evaluation/checkNumberPlate/${numberPlate}?loadPreviousEvals=${loadPreviousEvals}`,
      null,
    );
  }

  checkChassis(chassis: string, loadPreviousEvals: boolean = true) {
    return this.http.post<EvalVehicleLookupResponse>(
      `${environment.api}/evaluation/checkChassis/${encodeURIComponent(chassis)}?loadPreviousEvals=${loadPreviousEvals}`,
      null,
    );
  }

  sendBackToEvaluator(id, comments) {
    return this.http.post(
      `${environment.api}/evaluation/pricing/sendBackToEvaluator`,
      {
        id,
        comments,
      }
    );
  }

  updatePrice(req: UpdateEvalPriceRequest): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `${environment.api}/evaluation/updatePrice`,
      buildUpdatePricePayload(req),
    );
  }

  removeFromInventory(evalId: string, reason: string): Observable<DefaultStatus> {
    return this.http.delete<DefaultStatus>(
      `${environment.api}/evaluation/${evalId}/remove-from-inventory?reason=${reason}`    );
  }

  closePricing(id, comments) {
    return this.http.post(`${environment.api}/evaluation/pricing/close`, {
      id,
      comments,
    });
  }

  buyVehicle(id: string, comments: string) {
    return this.http.post(`${environment.api}/evaluation/buyVehicle`, {
      id,
      comments,
    });
  }

  returnToPricing(id: string, comments: string): Observable<VehicleUpdateStatus> {
    return this.http.post<VehicleUpdateStatus>(`${environment.api}/evaluation/returnToPricing`, {
      id,
      comments,
    });
  }

  sendPricingComment(id: string, comments: string) {
    return this.http.post(`${environment.api}/evaluation/pricing/comment`, {
      id,
      comments
    });
  }

  addComment(evalVehicleId: string, comment: string, isPrivate: boolean) {
    return this.http.post<any>(
      `${environment.api}/evaluation/${evalVehicleId}/addComment?isPrivate=${isPrivate}`,
      { comments: comment }
    );
  }

  editComment(evalVehicleId: string, currentComment: string, newComment: string, isPrivate: boolean) {
    return this.http.patch<any>(
      `${environment.api}/evaluation/${evalVehicleId}/editComment?isPrivate=${isPrivate}`,
      { currentComment: currentComment.trim(), comments: newComment.trim() }
    );
  }

  reactivate(id: string, comments: string) {
    return this.http.post(`${environment.api}/evaluation/reactivate`, {
      id,
      comments,
    });
  }

  getEvalsSummary(filter: EvaluationVehicleFilter): Observable<EvalVehicleSummary> {
    return this.http
      .post(`${environment.api}/evaluation/summary`, filter)
      .pipe(map((response: EvalVehicleSummary) => response));
  }

  evaluationPrice(vehicleId: string, filter: any): Observable<BuyPriceDetails> {
    return this.http.post(
      `${environment.api}/desk/buyPriceDetails/${vehicleId}`,
      filter
    ) as Observable<BuyPriceDetails>;
  }
  sellPrice(
    vehicleId: string,
    filter: any,
    evalVehicle = false
  ): Observable<SellPriceDetails> {
    let params = new HttpParams().append("evalVehicle", evalVehicle + "");
    return this.http.post(
      `${environment.api}/desk/sellPriceDetails/${vehicleId}`,
      filter,
      {
        params,
      }
    ) as Observable<SellPriceDetails>;
  }

  addImportedEvaluations(evaluationToSave: EvaluationImportItem[]
  ): Observable<VehicleUpdateStatus> {
    if (typeof evaluationToSave == "undefined")
      return this.http.post(`${environment.api}/evaluation/saveImportedEvals`, evaluationToSave)
        .pipe(map((response: VehicleUpdateStatus) => response));
  }

  getEvalHistorySummary(filter: EvaluationHistoryFilter): Observable<EvalHistorySummary> {
    return this.http
      .post(`${environment.api}/evaluation/summaryHistory`, filter)
      .pipe(map((response: EvalHistorySummary) => response));
  }

  getDefaultRangePickerOptions(): any {
    return {
      locale: {
        format: "DD/MM/YYYY",
        separator: " - ",
        applyLabel: "Aplicar",
        cancelLabel: "Cancelar",
        fromLabel: "De",
        toLabel: "Até",
        customRangeLabel: "Personalizado",
        daysOfWeek: ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"],
      },
      startDate: moment().date(1).startOf("day"),
      endDate: moment().startOf("day"),
      alwaysShowCalendars: true,
      dateLimit: { days: 365 },
      showCustomRangeLabel: false,
      ranges: {
        Hoje: [moment().startOf("day"), moment().startOf("day")],
        Ontem: [
          moment().subtract(1, "days").startOf("day"),
          moment().subtract(1, "days").startOf("day"),
        ],
        "Últimos 7 dias": [
          moment().subtract(7, "days").startOf("day"),
          moment().subtract(1, "days").startOf("day"),
        ],
        "Últimos 30 dias": [
          moment().subtract(30, "days").startOf("day"),
          moment().subtract(1, "days").startOf("day"),
        ],
        "Mês atual": [moment().date(1).startOf("day"), moment().startOf("day")],
        "Mês passado": [
          moment().subtract(1, "month").date(1).startOf("day"),
          moment().date(1).subtract(1, "days").startOf("day"),
        ],
      },
    };
  };

  getEvaluationRangePickerOptions(): any {
    return {
      locale: {
        format: "DD/MM/YYYY",
        separator: " - ",
        applyLabel: "Aplicar",
        cancelLabel: "Cancelar",
        fromLabel: "De",
        toLabel: "Até",
        customRangeLabel: "Personalizado",
        daysOfWeek: ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"],
      },
      autoUpdateInput: false,
      alwaysShowCalendars: true,
      dateLimit: { months: 2 },
      showCustomRangeLabel: false,
      ranges: {
        Hoje: [moment().startOf("day"), moment().startOf("day")],
        Ontem: [
          moment().subtract(1, "days").startOf("day"),
          moment().subtract(1, "days").startOf("day"),
        ],
        "Últimos 7 dias": [
          moment().subtract(7, "days").startOf("day"),
          moment().subtract(1, "days").startOf("day"),
        ],
        "Últimos 30 dias": [
          moment().subtract(30, "days").startOf("day"),
          moment().subtract(1, "days").startOf("day"),
        ],
        "Mês atual": [moment().date(1).startOf("day"), moment().startOf("day")],
        "Mês passado": [
          moment().subtract(1, "month").date(1).startOf("day"),
          moment().date(1).subtract(1, "days").startOf("day"),
        ],
      },
    };
  }

  sendNegotiationStatus(evalVehicleId: string, status: string): Observable<VehicleNumberPlateStatus> {
    return this.http.patch(`${environment.api}/evaluation/${evalVehicleId}/negotiationStatus/${status}`, null)
      .pipe(map((response: VehicleNumberPlateStatus) => response));
  }

  saveAudio(audio: Blob, id: any): Observable<FileUploadResult> {
    const formData = new FormData();
    formData.append('audio', audio);

    return this.http.post<FileUploadResult>(`${environment.api}/evaluation/saveAudio/${id}`, formData);
  }

  saveMedia(media: Blob, vehicleId: string, reportItem: string): Observable<FileUploadResult> {
    const formData = new FormData();
    formData.append('file', media);
    formData.append('reportItem', reportItem);

    return this.http.post<FileUploadResult>(`${environment.api}/evaluation/saveMedia/${vehicleId}`, formData);
  }

  saveImage(image: Blob, vehicleId: string): Observable<FileUploadResult> {
    const formData = new FormData();
    formData.append('file', image);

    return this.http.post<FileUploadResult>(
      this.imageUploadUrlService.getEvalVehicleImageUploadUrl(vehicleId),
      formData,
    );
  }

  downloadCSV(filter: EvaluationVehicleFilter): Observable<any> {
    return this.http.post(`${environment.api}/evaluation/downloadCSV`, filter, {
      headers: new HttpHeaders({
        Accept: 'application/csv',
      }),
      responseType: 'blob' as 'json'
    });
  }

  searchEvalAndLeadByNumberPlate(filter: EvaluationVehicleFilter): Observable<EvaluationLeadResponse>{
    return this.http
    .post(`${environment.api}/evaluation/getEvalsAndLeadsByNumberPlate`, filter)
    .pipe(map((response: EvaluationLeadResponse) => response));
  }

  getSegmentOptions<T extends "CAR" | "MOTORCYCLE" | "TRUCK">(segment: T): Observable<T extends "TRUCK" ? EvalTruckOptions : T extends "MOTORCYCLE" ? EvalMotorcycleOptions : EvalOptions> {
    if (!segment) segment = "CAR" as any;
    return this.http.get<T extends "TRUCK" ? EvalTruckOptions : T extends "MOTORCYCLE" ? EvalMotorcycleOptions : EvalOptions>(
      `${environment.api}/evaluation/options?segment=${segment}`
    );
  }

  sendOwnerPhoneConfirmationCode(request: OwnerPhoneConfirmationRequest): Observable<DefaultStatus> {

    return this.http.post<DefaultStatus>(
      `${environment.api}/evaluation/sendOwnerPhoneConfirmationCode`,
      request
    );
  }
  confirmReceipt(evalVehicleId: string): Observable<DefaultStatus> {
    return this.http.post<DefaultStatus>(
      `${environment.api}/evaluation/confirmReceipt`,
      { id: evalVehicleId }
    );
  }

  returnToNegotiation(evalVehicleId: string): Observable<DefaultStatus> {
    return this.http.post<DefaultStatus>(
      `${environment.api}/evaluation/returnToNegotiation`,
      { id: evalVehicleId }
    );
  }

  startPricing(evaluationId: string): Observable<VehicleUpdateStatus> {
    return this.http.post<VehicleUpdateStatus>(
      `${environment.api}/evaluation/pricing/startPricing/${evaluationId}`,
      null
    );
  }

  abandonPricing(evaluationId: string): Observable<VehicleUpdateStatus> {
    return this.http.post<VehicleUpdateStatus>(
      `${environment.api}/evaluation/pricing/abandonPricing/${evaluationId}`,
      null
    );
  }

  searchStock(filter: EvaluationVehicleFilter): Observable<EvaluationVehicleResponse> {
    return this.http.post<EvaluationVehicleResponse>(`${environment.api}/evaluation/search-stock`, filter)
      .pipe(map((response: EvaluationVehicleResponse) => this.mapEvaluationVehicleResponse(response)));
  }

  getStockSummary(filter: EvaluationVehicleFilter): Observable<InventorySummary> {
    return this.http.post<InventorySummary>(`${environment.api}/evaluation/stock-summary`, filter);
  }

  getAnalysisSummary(filter: EvaluationVehicleFilter): Observable<AnalysisSummary> {
    return this.http.post<AnalysisSummary>(`${environment.api}/evaluation/price-analysis`, filter);
  }

  getStockHealth(filter: EvaluationVehicleFilter): Observable<StockHealth> {
    return this.http.post<StockHealth>(`${environment.api}/evaluation/stock-health`, filter);
  }

  getAwaitingEntry(filter: EvaluationVehicleFilter): Observable<AwaitingEntrySummary> {
    return this.http.post<AwaitingEntrySummary>(`${environment.api}/evaluation/stock-total-awaiting-entry`, filter);
  }

  downloadStockCSV(filter: EvaluationVehicleFilter): Observable<HttpResponse<Blob>> {
    return this.http.post(`${environment.api}/evaluation/downloadStockCSV`, filter, {
      headers: new HttpHeaders({
        Accept: 'application/csv',
      }),
      observe: 'response',
      responseType: 'blob',
    });
  }

  duplicateEvaluation(vehicleId: string, evalDealerId: string, responsibleUserId: string): Observable<VehicleUpdateStatus> {
    return this.http.post<VehicleUpdateStatus>(`${environment.api}/evaluation/duplicate/${vehicleId}`, {
      evalDealerId,
      responsibleUserId,
    });
  }

  getUsersByEvalDealer(evalDealerId: string): Observable<{ id: string; name: string }[]> {
    return this.http.get<{ id: string; name: string }[]>(
      `${environment.api}/evaluation/usersByEvalDealer/${evalDealerId}`
    );
  }

  refreshWebPrice(vehicleId: string): Observable<DefaultStatus> {
    return this.http.get<DefaultStatus>(`${environment.api}/evaluation/refresh-price-web-eval-vehicle/${vehicleId}`)
    .pipe(map((response: DefaultStatus) => response));
  }

  getPricingUsers(): Observable<IdName[]> {
    return this.http
      .get<IdName[]>(`${environment.api}/evaluation/pricing/pricingUsers`)
      .pipe(map((response) => response));
  }

  private mapEvaluationVehicleResponse(response: EvaluationVehicleResponse): EvaluationVehicleResponse {
    return {
      ...response,
      data: response?.data?.map((vehicle) => this.mapEvaluationVehicle(vehicle)) ?? [],
    };
  }

  private mapEvaluationVehicle(response: Partial<EvaluationVehicle>): EvaluationVehicle {
    const vehicle = Object.assign(new EvaluationVehicle(), response);
    vehicle.segment = resolveEvaluationVehicleSegment(vehicle);
    const resolvedPrice = resolveEvaluationPriceValue(vehicle.price, vehicle.priceCent);

    if (resolvedPrice !== null) {
      vehicle.price = resolvedPrice;
    }

    if (vehicle.otherEvalsSameVehicle?.length) {
      vehicle.otherEvalsSameVehicle = vehicle.otherEvalsSameVehicle.map((item) => this.mapEvaluationVehicle(item));
    }

    return vehicle;
  }
}


export class UpdateEvalPriceRequest {
  id: string;
  price?: number;
  priceCent?: number;
  factoryBonus?: number;
  interventionPrice?: number;
  minSellPrice?: number;
  listPrice?: number;
  comments?: string;
  destination: string;
  finalEvalScore?: number;
  finalEvalScoreABC?: string;
  interventionPriceAccepted?: boolean;
  estimatedPrice?: boolean;
  tradeInMarketPrice?: number;
  tradeInDealerCosts?: number;
  evaluatorId?: string;
}

export class EvalVehicleSummary {
  notStarted: number;
  evaluating: number;
  returned: number;
  scheduled: number;
  waiting: number;
  pricing: number;
  returnedPricing?: number;
  returnedPricingAvgTime?: number;
  totalPricingTime?: number;
  totalPricingAvgTime?: number;
  pricingAvgTime: number;
  negotiation: number;
  totalNegotiationTime?: number;
  negotiationAvgTime: number;
  negotiationStatuses: Map<NegotiationStatus, number>;
  fastQuoteNoOffer: number;
  fastQuoteWithOffer: number;
  fastQuoteExpiredNoOffer: number;
  fastQuoteExpiredWithOffer: number;
  inFastQuoteNoOffer: number;
  inFastQuoteWithOffer: number;
  inFastQuoteExpiredNoOffer: number;
  inFastQuoteExpiredWithOffer: number;
  totalTimeDifference?: number;
  awaitingEntry: number;
  rescueSentToStore?: number;
}
export enum EvaluationImportStatus {
  OK = "ok",
  UNKNOWN_ERROR = "Erro desconhecido. Atualize a página e tente novamente.",
  UNKNOWN_OEM_DEALER = "Vendedor desconhecido. Escolha apenas um vendedor no seu perfil.",
  EMPTY_FILE = "Arquivo vazio. Por favor, escolha outro arquivo.",
  INCORRECT_FORMAT = "Arquivo com formato inválido. Por favor, siga o formato definido no arquivo de exemplo.",
}

export class EvaluationImportItem {
  rowNumber: number;
  evalStatus: StatusEvaluation;
  evalImportItemStatus: string;
  numberPlate: string;
  scheduledDate: Date;
  type: EvalTypeLabel;
  evalDealerId: string;
  evalDealerName: string;
  km: number;
  renavam: string;
  chassis: string;
  passengers: number;
  owner: Owner;
  reportUrl: string;
  reportTitle: string;
  reportStatus: ReportStatus;
  finalEvalScore: number;
  price: number;
  factoryBonus: number;
  minSellPrice: number;
  listPrice: number;
  destination: string;
  applicantName: string;
  applicantId: string;
  requiredFields: RequiredFields[];
}

export enum RequiredFields {
  PRICE = "PRICE",
  FINAL_EVAL_SCORE = "FINAL_EVAL_SCORE",
  KM = "KM",
  RENAVAM = "RENAVAM",
  DESTINATION = "DESTINATION",
  APPLICANT = "APPLICANT"
}

export class EvaluationImportResult {
  status: EvaluationImportStatus;
  success: boolean;
  ignoredLines: number;

  confirmed: EvaluationImportItem[];
  warning: EvaluationImportItem[];
  error: EvaluationImportItem[];
}

export enum EvaluationUpdatePriceDeleteStatus {
  OK = 'Arquivo importado com sucesso',
  UNKNOWN_ERROR = 'Erro desconhecido. Atualize a página e tente novamente.',
  UNKNOWN_OEM_DEALER = 'Grupo de concessionário desconhecido ou não selecionado.',
  EMPTY_FILE = 'Arquivo vazio',
  INCORRECT_FORMAT = 'Formato incorreto',
  PARTIAL_IMPORT = 'Arquivo importado parcialmente',
  NOTHING_IMPORTED = 'Nenhum dado importado'
}

export class EvaluationUpdateStatus {
  id?: string;
  status?: string;
  success?: boolean;
}

export class EvalHistorySummary {
  cancelled?: number; //block 1
  expired?: number;  //block 1
  customerGaveUp?: number; //block 2
  creditReproved?: number; //block 2
  noReturn?: number; //block 2
  pricing?: number; //block 2
  thirdPartySale?: number; //block 2
  precautionaryReproved?: number; //block 2
  productUnavailable?: number; //block 2
  businessNoExchange?: number; //block 2
  purchasedFromAnotherDealer?: number; //block 2
  withoutJustification?: number; //block 2
  showroom?: number; // block 3
  tradeIn?: number; // block 3
  soldFastQuote?: number; //block 4
}
